import { useEffect, useState } from 'react';
import {
  api, DeskError, type TemplateDetail, type TemplatePreview, type TemplateProblem, type TemplateVersion,
} from '../lib/api';
import { useResource } from '../lib/use-resource';
import {
  caseFields, caseLabel, countsLine, draftChanged, draftFrom, editOf, filledFromLine, placeholderParts, putBackReason,
  ruleLabel, rulesLine, STATE_LABEL, versionLine, whenItSends, withCase, type TemplateDraft,
} from '../lib/templates';

/**
 * One template (spec 014): as the customer reads it, as stored, when it sends, how often it went, and what would stop
 * it sending (Stage 1) — and, for the operator, its wording edited (Stage 2): subject, body and each case wording, the
 * rules line and the preview redrawn as he types, a reason, Save; every earlier version, any of which he can put back
 * through the same checks. Nothing here changes a template's row — a row changes only by a migration that records a
 * decision (TMPL-10). The desk judges every save (saveProblems); this screen only shows what it says.
 */
export function TemplatePanel({ id, operator, onSaved }: { id: number; operator: boolean; onSaved: () => void }) {
  const [typed, setTyped] = useState('');
  const [account, setAccount] = useState('');
  const one = useResource<TemplateDetail>(() => api.templates.get(id, account || undefined), { deps: [id, account] });
  const t = one.data && one.data.id === id ? one.data : null;
  const [draft, setDraft] = useState<TemplateDraft | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [restoredFrom, setRestoredFrom] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [problems, setProblems] = useState<TemplateProblem[] | null>(null);
  const [previews, setPreviews] = useState<TemplatePreview[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // The live rules line and preview (TMPL-13): 400 ms after the last keystroke, from the text as it stands.
  useEffect(() => {
    if (!draft || !t) return;
    let gone = false;
    setChecking(true);
    const timer = setTimeout(() => {
      api.templates.check(id, editOf(draft), account || undefined)
        .then((r) => { if (!gone) { setProblems(r.problems); setPreviews(r.previews); } })
        .catch((e: Error) => { if (!gone) setError(e.message); })
        .finally(() => { if (!gone) setChecking(false); });
    }, 400);
    return () => { gone = true; clearTimeout(timer); };
  }, [draft, id, account]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!t) {
    return <section className="panel knowledge-editor"><p className="list-empty">{one.error?.message ?? 'Reading the template…'}</p></section>;
  }
  const editing = draft !== null;
  const shown = editing && previews ? previews : t.previews;
  const many = shown.length > 1;

  const open = (v: TemplateVersion | null = null) => {
    setDraft(draftFrom(t, v));
    setBase(t.version_id);
    setRestoredFrom(v?.id ?? null);
    setReason(v ? putBackReason(v) : '');
    setProblems(null); setPreviews(null); setError(null); setConflict(false);
  };
  const close = () => { setDraft(null); setProblems(null); setPreviews(null); setError(null); setConflict(false); };
  const save = async () => {
    if (!draft) return;
    setBusy(true); setError(null);
    try {
      await api.templates.save(id, { ...editOf(draft), reason: reason.trim(), base_version: base, restored_from: restoredFrom });
      close();
      await one.refetch();
      onSaved();
    } catch (e) {
      // A refusal keeps his text to correct (TMPL-22); a save since he opened it keeps it too.
      const d = e as DeskError;
      if (d.status === 409) setConflict(true);
      const listed = (d.body as { problems?: TemplateProblem[] } | null)?.problems;
      if (listed) setProblems(listed);
      setError(d.message);
    } finally {
      setBusy(false);
    }
  };
  const blocking = (problems ?? []).filter((p) => p.rule !== 'unchanged');
  const changed = draft ? draftChanged(t, draft) : false;

  return (
    <section className="panel knowledge-editor template-panel">
      <div>
        <div className="mono eyebrow">T{t.id} · {STATE_LABEL[t.state]}{t.family ? ` · ${t.family}` : ''}</div>
        <h2 className="template-title">{t.subject ?? '(no subject)'}</h2>
        <p className="mono template-counts">{countsLine(t)}</p>
      </div>

      {!editing && t.problems.length > 0 && (
        <div className="knowledge-note template-problems" role="alert">
          <strong>Would be refused at send.</strong>
          <ul>{t.problems.map((p) => <li key={`${p.rule}:${p.words}`}><span className="mono">{ruleLabel(p.rule)}</span> — {p.says}</li>)}</ul>
        </div>
      )}

      {!editing && (
        <div className="template-edit-bar">
          {operator && t.editable
            ? <button type="button" className="btn-primary" onClick={() => open()}>Edit wording</button>
            : <span className="mono template-lock">Read-only: {!operator ? 'only the operator rewords a template' : t.locked}.</span>}
        </div>
      )}

      {editing && draft && (
        <div className="template-editor">
          {(t.pinned.length > 0 || t.footer) && (
            <div className="knowledge-note">
              <strong>These stay word for word.</strong> Their numbers are their rows&rsquo;; the footer is a compliance
              paragraph. Each changes only by migration with a decision.
              <ul>
                {t.pinned.map((p) => <li key={`${p.where}:${p.sentence}`}><span className="mono">{p.where}</span> {p.sentence}</li>)}
                {t.footer && <li><span className="mono">body</span> The Financial Hardship and dispute-resolution footer</li>}
              </ul>
            </div>
          )}
          {restoredFrom !== null && <p className="knowledge-note">Putting back an earlier version — nothing is saved until you press Save.</p>}
          {conflict && (
            <p className="knowledge-note"><strong>Saved elsewhere since you opened it.</strong> Your text is kept here; copy
              what you need, then <button type="button" className="btn-secondary" onClick={() => { close(); void one.refetch(); }}>
              Discard my edits and reload</button></p>
          )}
          <label className="knowledge-field">
            <span className="mono">Subject</span>
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </label>
          <label className="knowledge-field knowledge-body">
            <span className="mono">Body · it may use {t.allowed_placeholders.map((k) => `[${k}]`).join(' ')}</span>
            <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} spellCheck />
          </label>
          {caseFields(draft.cases).map((f) => (
            <label key={`${f.name}:${f.key}`} className="knowledge-field">
              <span className="mono">{f.label}</span>
              <input value={f.value} onChange={(e) => setDraft(withCase(draft, f.name, f.key, e.target.value))} />
            </label>
          ))}
          <div className={`knowledge-note template-rules${blocking.length ? ' template-problems' : ''}`} role="status">
            <strong>{rulesLine(changed || problems?.length ? problems : null, checking) || 'Edit the wording above.'}</strong>
            {blocking.length > 0 && (
              <ul>{blocking.map((p, i) => <li key={`${p.rule}:${p.words}:${i}`}><span className="mono">{ruleLabel(p.rule)}</span> — {p.says}</li>)}</ul>
            )}
          </div>
          <div className="knowledge-save">
            <input className="knowledge-reason" placeholder="Why — kept with the version (required)" value={reason}
              onChange={(e) => setReason(e.target.value)} />
            <button type="button" className="btn-primary" onClick={save}
              disabled={busy || !changed || !reason.trim() || blocking.length > 0 || checking}>
              {busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          </div>
          {error && <p className="list-error">{error}</p>}
        </div>
      )}

      <section className="template-section">
        <h3 className="mono">When it sends</h3>
        <ul className="template-when">{whenItSends(t, t.row).map((l) => <li key={l}>{l}</li>)}</ul>
      </section>

      <section className="template-section">
        <h3 className="mono">{editing ? 'As the customer would read your text' : 'As the customer reads it'}</h3>
        <form className="template-account" onSubmit={(e) => { e.preventDefault(); setAccount(typed.trim()); }}>
          <span className="template-from">{filledFromLine(t.filled_from)}</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="numeric" placeholder="Account number"
            aria-label="Fill from account number" />
          <button type="submit" className="btn-secondary">Fill</button>
          {account && <button type="button" className="btn-secondary" onClick={() => { setTyped(''); setAccount(''); }}>Reset</button>}
        </form>
        {one.error && account && <p className="list-error">{one.error.message}</p>}
        {shown.map((p) => (
          <article key={p.case ?? 'one'} className="template-preview">
            {many && <h4>{caseLabel(p.case)}</h4>}
            <div className="template-subject"><span className="mono">Subject</span> {p.subject}</div>
            <pre className="template-text">{p.text}</pre>
            {p.unfilled.length > 0 && (
              <p className="mono knowledge-blocked">Would not send to this account: {p.unfilled.map((k) => `[${k}]`).join(', ')} still
                in the text.</p>
            )}
          </article>
        ))}
      </section>

      <details className="template-section">
        <summary className="mono">As stored · {t.placeholders.length} placeholder{t.placeholders.length === 1 ? '' : 's'}: {t.placeholders.map((k) => `[${k}]`).join(' ') || 'none'}</summary>
        <div className="template-subject"><span className="mono">Subject</span> <Marked text={t.subject ?? ''} /></div>
        <pre className="template-text"><Marked text={t.body} /></pre>
        {Object.entries(t.cases).map(([name, vars]) => (
          <div key={name} className="template-case">
            <h4>{caseLabel(name)} <span className="mono">({name})</span></h4>
            {Object.entries(vars).map(([k, v]) => <p key={k}><span className="mono template-ph">[{k}]</span> = {v}</p>)}
          </div>
        ))}
      </details>

      <details className="template-section knowledge-history">
        <summary className="mono">History · {t.versions.length} version{t.versions.length === 1 ? '' : 's'}</summary>
        {t.versions.length === 0 && <p className="template-counts">Never saved here: the text is as the migrations left it.</p>}
        <ol>
          {t.versions.map((v) => (
            <li key={v.id}>
              <details>
                <summary>{versionLine(v)}</summary>
                <div className="template-subject"><span className="mono">Subject</span> {v.subject}</div>
                <pre className="knowledge-old">{v.body}</pre>
                {v.cases && Object.entries(v.cases).map(([name, vars]) => (
                  <p key={name} className="template-case"><span className="mono">{name}</span> {Object.values(vars).join(' · ')}</p>
                ))}
                {operator && t.editable && !editing && (
                  <button type="button" className="btn-secondary" onClick={() => open(v)}>Put back</button>
                )}
              </details>
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}

function Marked({ text }: { text: string }) {
  return <>{placeholderParts(text).map(([part, ph], i) => (ph ? <span key={i} className="template-ph">{part}</span> : part))}</>;
}
